import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import StagingFileImport from '../components/StagingFileImport';
import StagingReviewQueue from '../components/StagingReviewQueue';
import StagingStudentEditPanel from '../components/StagingStudentEditPanel';
import { ThemeButton } from '../components/ThemeWrapper';
import useStagingReview, { STAGING_FLAG } from '../hooks/useStagingReview';
import { isPrivilegedStaffRole } from '../utils/permissions';

export default function AdminDataStagingReviewPage() {
  const { user } = useContext(AuthContext);
  const userRole = user?.role || user?.user?.role;
  const navigate = useNavigate();

  const review = useStagingReview();

  useEffect(() => {
    if (!isPrivilegedStaffRole(userRole)) {
      navigate('/dashboard');
    }
  }, [userRole, navigate]);

  if (review.enabled === false) {
    return (
      <StaffPageShell title="Data staging review" wide>
        <p className="theme-text-secondary">
          Staging is turned off. Enable <code>{STAGING_FLAG}</code> under Feature flags, then
          import a CSV/Excel file here so incomplete or ambiguous rows appear for admin action.
        </p>
      </StaffPageShell>
    );
  }

  return (
    <StaffPageShell
      title="Data staging review"
      subtitle="Same Edit User form and save — fix review rows then Save & next"
      wide
      actions={
        <ThemeButton type="button" onClick={() => review.loadQueue()} disabled={review.loading}>
          Refresh
        </ThemeButton>
      }
    >
      {review.error && (
        <div className="mb-4 p-3 rounded theme-error-bg theme-error-text text-sm">
          {review.error}
        </div>
      )}
      {review.message && (
        <div className="mb-4 p-3 rounded theme-card theme-text text-sm">{review.message}</div>
      )}

      <StagingFileImport onImported={review.onImported} />

      <p className="theme-text-secondary text-sm mb-4">
        Duplicate emails = same address on 2+ upload rows. Names only = a name with no email,
        kept in staging until you add an address. Already in database = one row matching SQL.
        Other buckets are profile fields — counts match Student profile completeness
        {review.completenessStats
          ? ` (${review.completenessStats.incompleteCount} incomplete / ${review.completenessStats.totalActive} active)`
          : ''}
        .
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 staging-review-detail rounded-lg p-3 max-h-[75vh] overflow-y-auto border">
          <h2 className="font-semibold theme-text mb-2">Review by field</h2>
          <StagingReviewQueue
            items={review.items}
            selectedId={review.selectedId}
            onSelect={review.setSelectedId}
            loading={review.loading}
            completenessStats={review.completenessStats}
            completenessItems={review.completenessItems}
            selectedFieldKey={review.selectedFieldKey}
            onSelectField={review.selectField}
            completenessLoading={review.completenessLoading}
          />
        </div>

        <div className="lg:col-span-2 staging-review-detail rounded-lg p-4 max-h-[75vh] overflow-y-auto border">
          <StagingStudentEditPanel
            selected={review.selected}
            acting={review.acting}
            onApply={review.applyDocument}
            onSkip={review.skipDocument}
            onNext={review.goNextOnly}
            hasNext={review.hasNext}
            categoryPosition={review.categoryPosition}
            showJson={review.showJson}
            onToggleJson={review.toggleJson}
            enrollmentDecision={review.enrollmentDecision}
            onEnrollmentDecisionChange={review.setEnrollmentDecision}
            queueItems={review.items}
            onSelectDocument={review.setSelectedId}
            onClusterAction={review.decideDuplicateCluster}
            focusFieldKey={review.selectedFieldKey}
            duplicateCluster={review.duplicateCluster}
          />
        </div>
      </div>
    </StaffPageShell>
  );
}
